import React, { useCallback, useMemo, useState } from 'react';

import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import { navIconCollapsePanel as CollapsePanelIcon } from 'containers/App/navIcons';
import { useAppSelector } from 'hooks/redux';
import { isMac } from 'utils/utils';

import { useNavExpansion } from '../NavExpansionContext';
import { navTooltipProps, useNavItemStyles } from '../commonNavStyles';

export const NavToggleButton = React.memo(function NavToggleButton(): React.JSX.Element {
  const { classes, cx } = useNavItemStyles();
  const { expanded, toggle } = useNavExpansion();
  const globalHotKeysEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'globalHotKeys'));
  const shortcutLabel = useMemo(() => (isMac() ? '\u2325\u21e7N' : 'Shift+Alt+N'), []);

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState(expanded ? 'Collapse' : 'Expand');
  const [isTooltipSuppressed, setIsTooltipSuppressed] = useState(false);

  const handleClick = useCallback(() => {
    setIsTooltipSuppressed(true);
    toggle('button');
  }, [toggle]);
  const handleTooltipRelease = useCallback(() => {
    setIsTooltipOpen(false);
    setIsTooltipSuppressed(false);
  }, []);

  return (
    <Tooltip
      {...navTooltipProps}
      title={globalHotKeysEnabled ? `${tooltipTitle} (${shortcutLabel})` : tooltipTitle}
      open={isTooltipOpen && !isTooltipSuppressed}
      onOpen={() => {
        setTooltipTitle(expanded ? 'Collapse' : 'Expand');
        setIsTooltipOpen(true);
      }}
      onClose={() => setIsTooltipOpen(false)}
      slotProps={{
        transition: isTooltipSuppressed ? { timeout: { exit: 0 } } : undefined,
      }}
    >
      <ButtonBase
        id='nav-toggle-button'
        className={cx(classes.navMenuItem, classes.navToggleButton)}
        onClick={handleClick}
        onMouseLeave={handleTooltipRelease}
        onBlur={handleTooltipRelease}
        aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
        aria-expanded={expanded}
        aria-controls='application-header-bar'
        aria-keyshortcuts={globalHotKeysEnabled ? 'Shift+Alt+N' : undefined}
      >
        <span className={classes.navBarIconSpan}>
          <CollapsePanelIcon
            aria-hidden
            className={cx(classes.navIcon, classes.navToggleIcon, !expanded && classes.navToggleIconCollapsed)}
          />
        </span>
      </ButtonBase>
    </Tooltip>
  );
});
